import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { SELECT_CLS, apiError } from "@/lib/network";

export const MEMBER_KINDS = [["olt", "OLT"], ["otb", "OTB"], ["splitter", "Splitter"]];

const run = async (fn, ok, done, setBusy) => {
  setBusy(true);
  try { await fn(); toast.success(ok); done(); } catch (err) { toast.error(apiError(err), { duration: 8000 }); } finally { setBusy(false); }
};

// Tambah anggota: perangkat yang sudah anggota SERVER lain hanya bisa dipindahkan (eksplisit).
export function AddMemberDialog({ open, server, onClose, onDone }) {
  const [kind, setKind] = useState("olt");
  const [devs, setDevs] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setKind("olt"); setQ(""); } }, [open]);
  useEffect(() => {
    if (!open) return;
    setDevs(null); setPick("");
    api.get(`/network/server-devices/${kind}`).then((r) => setDevs(r.data)).catch(() => setDevs([]));
  }, [open, kind]);
  const list = useMemo(() => (devs || []).filter((d) => !q || [d.kode, d.nama].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))), [devs, q]);
  const dev = (devs || []).find((d) => d.id === pick);
  const other = dev?.server_id && dev.server_id !== server.id;
  const otherType = other && dev.container_label !== "SERVER";
  const submit = () => run(
    () => (other ? api.put(`/network/server-members/${kind}/${dev.id}`, { server_id: server.id })
      : api.post("/network/server-members", { kind, device_id: dev.id, server_id: server.id })),
    other ? `${dev.kode} dipindahkan dari ${dev.server_kode} ke ${server.kode}` : `${dev.kode} ditambahkan ke ${server.kode}`, onDone, setBusy);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="server-add-member-dialog">
        <DialogHeader><DialogTitle>Tambah Anggota ke {server.kode}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Jenis Perangkat</Label>
            <select className={`${SELECT_CLS} w-full`} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="server-add-member-kind">
              {MEMBER_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div><Label>Perangkat</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode / nama…" className="mb-1 h-9" data-testid="server-add-member-search" />
            <select className={`${SELECT_CLS} w-full`} value={pick} onChange={(e) => setPick(e.target.value)} disabled={!devs} data-testid="server-add-member-device">
              <option value="">{devs ? `— Pilih (${list.length}) —` : "Memuat…"}</option>
              {list.map((d) => <option key={d.id} value={d.id} disabled={d.server_id === server.id}>
                {d.kode} · {d.nama}{d.server_kode ? ` (anggota ${d.container_label} ${d.server_kode})` : ""}</option>)}</select></div>
          {other && <p className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] p-2 text-xs text-[#92400E]" data-testid="server-add-member-other">
            {otherType ? `${dev.kode} saat ini anggota ${dev.container_label} ${dev.server_kode}. Keluarkan dari ${dev.container_label} terlebih dahulu.`
              : `${dev.kode} saat ini anggota SERVER ${dev.server_kode}. Simpan untuk memindahkannya ke ${server.kode}.`}</p>}
          <p className="text-xs text-[#6B7280]">Keanggotaan SERVER hanya pengelompokan; kode, koordinat, status, port, dan relasi topologi perangkat tidak berubah.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
          <Button disabled={!dev || busy || otherType} onClick={submit} data-testid="server-add-member-submit">{other ? "Pindahkan ke SERVER ini" : "Tambahkan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MoveMemberDialog({ member, server, onClose, onDone }) {
  const [servers, setServers] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) return;
    setTo(""); setServers(null);
    api.get("/network/servers").then((r) => setServers(r.data.filter((s) => s.id !== server.id))).catch(() => setServers([]));
  }, [member, server.id]);
  const t = (servers || []).find((s) => s.id === to);
  return (
    <Dialog open={!!member} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="server-move-member-dialog">
        <DialogHeader><DialogTitle>Pindahkan {member?.label} {member?.kode}</DialogTitle></DialogHeader>
        <Label>SERVER Tujuan</Label>
        <select className={`${SELECT_CLS} w-full`} value={to} onChange={(e) => setTo(e.target.value)} disabled={!servers} data-testid="server-move-member-target">
          <option value="">{servers ? (servers.length ? "— Pilih —" : "Tidak ada SERVER aktif lain") : "Memuat…"}</option>
          {(servers || []).map((s) => <option key={s.id} value={s.id}>{s.kode} · {s.nama}</option>)}</select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
          <Button disabled={!t || busy} data-testid="server-move-member-submit"
                  onClick={() => run(() => api.put(`/network/server-members/${member.kind}/${member.id}`, { server_id: to }),
                    `${member.kode} dipindahkan ke ${t.kode}`, onDone, setBusy)}>Pindahkan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RemoveMemberDialog({ member, server, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog open={!!member} onOpenChange={(v) => !v && !busy && onClose()}>
      <AlertDialogContent data-testid="server-remove-member-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Keluarkan {member?.label} {member?.kode}?</AlertDialogTitle>
          <AlertDialogDescription>Perangkat dikeluarkan dari SERVER {server.kode}. Data dan relasi topologi perangkat tidak berubah.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="server-remove-member-cancel">Batal</AlertDialogCancel>
          <Button disabled={busy} data-testid="server-remove-member-confirm"
                  onClick={() => run(() => api.delete(`/network/server-members/${member.kind}/${member.id}`), `${member.kode} dikeluarkan dari ${server.kode}. Lokasi peta belum ditentukan — tetapkan koordinat lewat Edit atau Geser Perangkat agar perangkat dan ujung kabel terkait tampil di peta.`, onDone, setBusy)}>
            Keluarkan</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
